/** @jsxImportSource @emotion/react */

import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import PageTitle from '../../components/PageTitle'
import Seo from '../../components/Seo'
import { useAuthContext } from '../../context/auth.context'
import { GET_COMPANIES_NAMES } from '../../graphql/company.graphql'
import { GET_PROJECTS } from '../../graphql/project.graphql'
import { GET_CATEGORIES } from '../../graphql/category.graphql'
import { GET_USERS } from '../../graphql/user.graphql'
import {
  ESTIMATE_REQUEST_CREATE,
  ESTIMATE_REQUEST_DECIDE,
  GET_ESTIMATE_REQUESTS,
  GET_MY_ESTIMATE_REQUESTS,
} from '../../graphql/estimateRequest.graphql'

const currentMonth = () => new Date().toISOString().slice(0, 7)

const formatDate = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pl-PL')
  } catch (error) {
    return '—'
  }
}

const decisionAt = (request) => {
  if (request.approvedAt) return request.approvedAt
  const entry = [...(request.history || [])].reverse().find((item) => item.decision === 'APPROVE' || item.decision === 'REJECT')
  return entry?.at || null
}

const EstimateRequests = () => {
  const { userId, userRole } = useAuthContext()
  const isDecisionMaker = userRole >= 4
  const [form, setForm] = useState({
    companyId: '', projectId: '', categoryId: '', personId: userId || '',
    month: currentMonth(), timezone: 'Europe/Warsaw', proposedMinutes: '', reason: '',
  })
  const companies = useQuery(GET_COMPANIES_NAMES).data?.getCompanies || []
  const projectsQuery = useQuery(GET_PROJECTS)
  const categories = useQuery(GET_CATEGORIES).data?.getCategories || []
  const users = useQuery(GET_USERS).data?.getUsers || []
  const requestsQuery = useQuery(isDecisionMaker ? GET_ESTIMATE_REQUESTS : GET_MY_ESTIMATE_REQUESTS)
  const [createRequest, { loading: creating }] = useMutation(ESTIMATE_REQUEST_CREATE, {
    refetchQueries: [{ query: GET_MY_ESTIMATE_REQUESTS }],
  })
  const [decideRequest] = useMutation(ESTIMATE_REQUEST_DECIDE, {
    refetchQueries: [{ query: GET_ESTIMATE_REQUESTS }],
  })
  const visibleProjects = useMemo(() => {
    const projects = projectsQuery.data?.getProjects || []
    return projects.filter((project) => !form.companyId || project.company?._id === form.companyId)
  }, [projectsQuery.data, form.companyId])
  const requests = requestsQuery.data?.[isDecisionMaker ? 'getEstimateRequests' : 'getMyEstimateRequests'] || []

  const update = (name) => (event) => setForm({ ...form, [name]: event.target.value })
  const submit = async (event) => {
    event.preventDefault()
    try {
      await createRequest({ variables: { input: { ...form, proposedMinutes: Number(form.proposedMinutes), projectId: form.projectId || null, idempotencyKey: `estimate-request:${userId}:${Date.now()}` } } })
      toast.success('Prośba o Estimate została wysłana')
      setForm({ ...form, proposedMinutes: '', reason: '' })
    } catch (error) { toast.error(error.message) }
  }
  const decide = async (request, decision) => {
    let approvedMinutes = null
    if (decision === 'APPROVE') {
      const raw = window.prompt('Zatwierdzone minuty', String(request.proposedMinutes))
      if (raw === null) return
      const trimmed = raw.trim()
      if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
        toast.error('Zatwierdzone minuty muszą być liczbą całkowitą większą od zera')
        return
      }
      approvedMinutes = Number(trimmed)
    }
    const decisionReason = window.prompt('Powód decyzji')
    if (!decisionReason?.trim()) return
    try {
      await decideRequest({ variables: { id: request._id, input: { decision, approvedMinutes, decisionReason: decisionReason.trim(), expectedVersion: request.version } } })
      toast.success('Decyzja zapisana')
    } catch (error) { toast.error(error.message) }
  }

  return <>
    <Seo title='Estimate requests' />
    <PageTitle><h1>Prośby o Estimate</h1></PageTitle>
    {!isDecisionMaker && <Paper css={sPanel}>
      <form onSubmit={submit} css={sForm}>
        <FormControl><InputLabel>Firma</InputLabel><Select value={form.companyId} onChange={update('companyId')} required>{companies.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Projekt</InputLabel><Select value={form.projectId} onChange={update('projectId')}>{visibleProjects.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Kategoria</InputLabel><Select value={form.categoryId} onChange={update('categoryId')} required>{categories.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Osoba</InputLabel><Select value={form.personId} onChange={update('personId')} required>{users.filter((item) => item.isActive).map((item) => <MenuItem key={item._id} value={item._id}>{item.firstName} {item.lastName}</MenuItem>)}</Select></FormControl>
        <TextField label='Miesiąc' type='month' value={form.month} onChange={update('month')} required InputLabelProps={{ shrink: true }} />
        <TextField label='Minuty' type='number' value={form.proposedMinutes} onChange={update('proposedMinutes')} required inputProps={{ min: 1 }} />
        <TextField label='Uzasadnienie' value={form.reason} onChange={update('reason')} required multiline />
        <Button type='submit' variant='contained' color='primary' disabled={creating}>Wyślij prośbę</Button>
      </form>
    </Paper>}
    <Paper css={sPanel}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Wysłano</TableCell>
            <TableCell>Decyzja</TableCell>
            <TableCell>Ścieżka</TableCell>
            <TableCell>Osoba</TableCell>
            <TableCell>Proponowane</TableCell>
            <TableCell>Zatwierdzone</TableCell>
            <TableCell>Uzasadnienie prośby</TableCell>
            <TableCell>Uzasadnienie decyzji</TableCell>
            <TableCell>Akcja</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request._id}>
              <TableCell>{request.status}</TableCell>
              <TableCell>{formatDate(request.createdAt)}</TableCell>
              <TableCell>{formatDate(decisionAt(request))}</TableCell>
              <TableCell>{request.company?.name} / {request.project?.name || '—'} / {request.category?.name}</TableCell>
              <TableCell>{request.person?.firstName} {request.person?.lastName}</TableCell>
              <TableCell>{request.proposedMinutes}</TableCell>
              <TableCell>{request.approvedMinutes || '—'}</TableCell>
              <TableCell>{request.reason}</TableCell>
              <TableCell>{request.decisionReason || '—'}</TableCell>
              <TableCell>
                {isDecisionMaker && request.status === 'PENDING' && <>
                  <Button onClick={() => decide(request, 'APPROVE')} color='primary'>Akceptuj</Button>
                  <Button onClick={() => decide(request, 'REJECT')} color='secondary'>Odrzuć</Button>
                </>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {requests.length === 0 && <div css={sEmpty}>Brak próśb.</div>}
    </Paper>
  </>
}

const sPanel = { marginBottom: '1rem', padding: '1rem', overflowX: 'auto' }
const sForm = { display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'center' }
const sEmpty = { padding: '1rem', color: '#666' }

export default EstimateRequests
